import { stringify as stringifyCsv } from "csv-stringify/browser/esm/sync";
import { parse as parseCsv } from "csv-parse/browser/esm/sync";
import { unknownToError } from "@dhau/lang-extras";
import type { Container, PaletteColour } from "../inventory-shared/data.ts";

function createImportTemplate(
	basePlateSizes: readonly number[],
	colours: readonly PaletteColour[],
	containers: readonly Container[],
) {
	const sheet = [
		["Item Name", "Value"], // Header row
		// Container items
		...containers.flatMap((container) =>
			colours.map((colour) => [`${container.identifier}-${colour.id}`, ""]),
		),
		// Baseplates
		...basePlateSizes.map((size) => [`Baseplate-${size}x${size}`, ""]),
		// Paper types
		["Paper-A5", ""],
		["Paper-A4", ""],
		["Paper-A3", ""],
		["Paper-Letter", ""],
		// Other items
		["Brick separators", ""],
		["Packaging", ""],
		["Small flatpack box", ""],
		["Logo tile", ""],
		// Packaging items
		["Padded envelope", ""],
		["Single box", ""],
		["Double box", ""],
		["Triple box", ""],
	];
	return stringifyCsv(sheet);
}

async function fileToString(file: File): Promise<string> {
	const reader = new FileReader();
	return new Promise((resolve, reject) => {
		reader.onload = (event) => {
			if (!event.target?.result) {
				reject(new Error("Couln't read file"));
				return;
			}
			const {
				target: { result },
			} = event;
			if (typeof result === "string") {
				resolve(result);
				return;
			}

			const decoder = new TextDecoder("utf-8");
			resolve(decoder.decode(result));
		};
		reader.readAsText(file);
	});
}

async function parseImportCsv(
	content: string,
): Promise<readonly (readonly string[])[]> {
	return new Promise((resolve, reject) => {
		try {
			const result = parseCsv(content);
			resolve(result);
		} catch (e) {
			reject(unknownToError(e));
		}
	});
}

function importValueToNumber(
	value: string | undefined | null,
): number | undefined {
	if (value === "" || value === null || value === undefined) {
		return undefined;
	}
	const proposed = parseFloat(value);
	if (Number.isNaN(proposed)) {
		return undefined;
	}
	return proposed;
}

async function importToValues(
	basePlateSizes: readonly number[],
	colours: readonly PaletteColour[],
	containers: readonly Container[],
	file: File,
): Promise<Record<string, unknown>> {
	const content = await fileToString(file);
	const rows = await parseImportCsv(content);

	// Skip header row
	const dataRows = rows.slice(1);

	// Create a map of item names to their values
	const itemMap = new Map(dataRows.map((row) => [row[0], row[1]]));

	// Process containers
	const containerValues = Object.fromEntries(
		containers.map((container) => [
			container.identifier,
			[
				undefined,
				...colours.map((colour) => {
					const itemName = `${container.identifier}-${colour.id}`;
					return importValueToNumber(itemMap.get(itemName));
				}),
			],
		]),
	);

	// Process baseplates
	const baseplateValues = Object.fromEntries(
		basePlateSizes.map((size) => {
			const itemName = `Baseplate-${size}x${size}`;
			return [size, importValueToNumber(itemMap.get(itemName))];
		}),
	);

	return {
		containers: containerValues,
		baseplates: baseplateValues,
		paperA5: importValueToNumber(itemMap.get("Paper-A5")),
		paperA4: importValueToNumber(itemMap.get("Paper-A4")),
		paperA3: importValueToNumber(itemMap.get("Paper-A3")),
		paperLetter: importValueToNumber(itemMap.get("Paper-Letter")),
		brickSeparators: importValueToNumber(itemMap.get("Brick separators")),
		packaging: importValueToNumber(itemMap.get("Packaging")),
		smallFlatpackBox: importValueToNumber(itemMap.get("Small flatpack box")),
		logoTile: importValueToNumber(itemMap.get("Logo tile")),
		paddedEnvelope: importValueToNumber(itemMap.get("Padded envelope")),
		singleBox: importValueToNumber(itemMap.get("Single box")),
		doubleBox: importValueToNumber(itemMap.get("Double box")),
		tripleBox: importValueToNumber(itemMap.get("Triple box")),
	};
}

export { createImportTemplate, importToValues };
